// Tri-state SoilSight onboarding state, persisted as a single user_settings key
// (SOIL_SIGHT_ONBOARDING_SETTING). Absent = legacy user who never went through the flow.

export type SoilSightOnboardingInProgress = {
  status: "in-progress";
  startedAt: string;
  farmId: string;
  importedFieldId?: string;
  importBannerDismissed?: boolean;
  /** True once the user passed the landing page (or acted on step 1) — later entries skip it. */
  landingSeen?: boolean;
};

export type SoilSightOnboardingCompleted = {
  status: "completed";
};

export type SoilSightOnboardingState = SoilSightOnboardingInProgress | SoilSightOnboardingCompleted;

export const parseSoilSightOnboardingState = (raw: unknown): SoilSightOnboardingState | null => {
  if (typeof raw !== "string" || raw.length === 0) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const candidate = parsed as {
      status?: unknown;
      startedAt?: unknown;
      farmId?: unknown;
      importedFieldId?: unknown;
      importBannerDismissed?: unknown;
      landingSeen?: unknown;
    };

    if (candidate.status === "completed") {
      return { status: "completed" };
    }

    if (candidate.status === "in-progress" && typeof candidate.startedAt === "string" && typeof candidate.farmId === "string") {
      return {
        status: "in-progress",
        startedAt: candidate.startedAt,
        farmId: candidate.farmId,
        importedFieldId: typeof candidate.importedFieldId === "string" ? candidate.importedFieldId : undefined,
        importBannerDismissed: candidate.importBannerDismissed === true,
        landingSeen: candidate.landingSeen === true,
      };
    }
  } catch {
    // Malformed value — treat as absent so the user falls back to the safe (landing) path.
  }

  return null;
};

export const serializeSoilSightOnboardingState = (state: SoilSightOnboardingState): string => JSON.stringify(state);

// Legacy users (project + scenarios, never went through the flow) count as onboarded. The
// in-progress key is written BEFORE project creation, so a draft user never lands in this branch.
export const isGrandfatheredSoilSightUser = ({
  hasProject,
  hasScenarios,
  onboardingState,
}: {
  hasProject: boolean;
  hasScenarios: boolean;
  onboardingState: SoilSightOnboardingState | null;
}): boolean => hasProject && hasScenarios && onboardingState === null;

// A draft only counts for the farm it was started on — projects are farm-scoped.
export const isDraftForFarm = (onboardingState: SoilSightOnboardingState | null, farmId: string | null | undefined): boolean =>
  onboardingState?.status === "in-progress" && Boolean(farmId) && onboardingState.farmId === farmId;

export type FieldImportRoute = "pending" | "direct" | "onboarding";

/** Import path for a field. Both routes overwrite the baseline, so an unreadable setting holds. */
export const resolveFieldImportRoute = ({
  isAccessLoading,
  isAccessError,
  isSettingLoading,
  isSettingError,
  hasProject,
  hasScenarios,
  onboardingState,
}: {
  isAccessLoading: boolean;
  isAccessError: boolean;
  isSettingLoading: boolean;
  isSettingError: boolean;
  hasProject: boolean;
  hasScenarios: boolean;
  onboardingState: SoilSightOnboardingState | null;
}): FieldImportRoute => {
  if (isAccessLoading || isSettingLoading || isSettingError) return "pending";

  // Fail-safe, as in the route gates: onboarding would delete change scenarios we cannot see.
  const isUnverifiedProjectOwner = isAccessError && hasProject && onboardingState?.status !== "in-progress";
  const isOnboarded =
    onboardingState?.status === "completed" || isUnverifiedProjectOwner || isGrandfatheredSoilSightUser({ hasProject, hasScenarios, onboardingState });

  return isOnboarded ? "direct" : "onboarding";
};

/**
 * Single source of truth for "landing already passed": either `completed`, or an in-progress draft
 * for THIS farm with `landingSeen` stamped. Every consumer choosing between the landing and step 1
 * must call this rather than re-derive it, so they cannot drift apart.
 */
export const hasPassedLandingForFarm = (onboardingState: SoilSightOnboardingState | null, farmId: string | null | undefined): boolean =>
  onboardingState?.status === "completed" ||
  (isDraftForFarm(onboardingState, farmId) && onboardingState?.status === "in-progress" && onboardingState.landingSeen === true);

export type InProgressDraftPatch = {
  farmId: string;
  importedFieldId?: string;
  importBannerDismissed?: boolean;
  landingSeen?: boolean;
};

/**
 * The ONE way to build an in-progress draft write: patches onto the existing draft so a writer
 * owning only some fields cannot wipe the others'. A draft for a DIFFERENT farm is replaced, not
 * merged. `undefined` fields keep their value; pass an explicit one to reset it.
 */
export const mergeInProgressDraft = (existing: SoilSightOnboardingState | null, patch: InProgressDraftPatch): SoilSightOnboardingInProgress => {
  const existingDraft = existing?.status === "in-progress" && existing.farmId === patch.farmId ? existing : null;

  return {
    status: "in-progress",
    startedAt: existingDraft?.startedAt ?? new Date().toISOString(),
    farmId: patch.farmId,
    importedFieldId: patch.importedFieldId ?? existingDraft?.importedFieldId,
    importBannerDismissed: patch.importBannerDismissed ?? existingDraft?.importBannerDismissed,
    landingSeen: patch.landingSeen ?? existingDraft?.landingSeen,
  };
};
